/**
 * useAssistantConnection Hook
 * Manages WebSocket connection for pipeline assistant
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AssistantEvent,
  TimelineMessage,
  TimelineItem,
  ConnectionState,
  OutgoingMessage,
  ToolCall,
  TimelineToolExecution,
} from '../types';

interface UseAssistantConnectionProps {
  pipelineId: number;
  provider?: string;
}

interface UseAssistantConnectionReturn {
  // Connection state
  isConnected: boolean;
  connectionState: ConnectionState;
  error: string | null;

  // Timeline
  timeline: TimelineItem[];
  streamingMessage: string | null;
  isStreaming: boolean;

  // Actions
  sendMessage: (content: string) => void;
  respondToToolRequest: (requestId: string, success: boolean, result?: any, error?: string) => void;
  stopStreaming: () => void;
  clearConversation: () => Promise<void>;

  // Tool state
  pendingToolCalls: Map<string, ToolCall>;
}

export function useAssistantConnection({
  pipelineId,
  provider = 'anthropic-claude-sonnet',
}: UseAssistantConnectionProps): UseAssistantConnectionReturn {
  // State
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    status: 'disconnected',
    provider: null,
    error: null,
    retryCount: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [pendingToolCalls, setPendingToolCalls] = useState<Map<string, ToolCall>>(new Map());

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageIdCounter = useRef(0);
  const streamingMessageRef = useRef<string | null>(null);

  // WebSocket URL construction
  const getWebSocketUrl = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const apiUrl = process.env.REACT_APP_API_URL;
    let baseWsUrl: string;

    if (apiUrl) {
      // Use configured API URL
      baseWsUrl = apiUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    } else if (process.env.NODE_ENV === 'production') {
      // Production: use frontend origin
      const frontendUrl = window.location.origin;
      baseWsUrl = frontendUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    } else {
      // Development: connect directly to backend
      baseWsUrl = 'ws://localhost:8000';
    }

    return `${baseWsUrl}/ws/pipeline/${pipelineId}/assistant?token=${token}&provider=${provider}`;
  }, [pipelineId, provider]);

  // Send message to WebSocket
  const send = useCallback((message: OutgoingMessage) => {
    console.log('Attempting to send WebSocket message:', message.type, 'readyState:', wsRef.current?.readyState);
    console.log('Full message:', JSON.stringify(message, null, 2));
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      console.log('Message sent successfully');
    } else {
      console.warn('WebSocket not connected, cannot send message:', message, 'readyState:', wsRef.current?.readyState);
    }
  }, []);

  // Handle incoming WebSocket events
  const handleEvent = useCallback((event: AssistantEvent) => {
    switch (event.type) {
      case 'connection_established':
        setConnectionState({
          status: 'connected',
          provider: event.data.provider,
          error: null,
          retryCount: 0,
        });
        console.log('Pipeline assistant connected:', event.data.message);
        
        // Load conversation history if provided
        if (event.data.conversation_history && event.data.conversation_history.length > 0) {
          console.log(`Loading ${event.data.conversation_history.length} messages from history`);
          const historyMessages: TimelineMessage[] = event.data.conversation_history.map((msg) => ({
            itemType: 'message',
            id: `history-${msg.id}`,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
          }));
          // Reverse to get chronological order (oldest first)
          setTimeline(historyMessages.reverse());
        }
        break;

      case 'text_delta':
        // Append to streaming message
        setIsStreaming(true);
        setStreamingMessage((prev) => {
          const updated = (prev || '') + event.delta;
          streamingMessageRef.current = updated; // Keep ref in sync
          return updated;
        });
        break;

      case 'tool_execution':
        // Tool execution started - finalize current message then add tool
        console.log('Tool execution:', event.tool_name, event.tool_params);
        
        // Use ref to get current streaming message synchronously
        const currentMessage = streamingMessageRef.current;
        
        setTimeline((prev) => {
          const items = [...prev];
          
          // Add finalized message if there is one
          if (currentMessage) {
            const msg: TimelineMessage = {
              itemType: 'message',
              id: `assistant-${messageIdCounter.current++}`,
              role: 'assistant',
              content: currentMessage,
              timestamp: new Date(),
            };
            items.push(msg);
          }
          
          // Add tool execution
          const tool: TimelineToolExecution = {
            itemType: 'tool',
            id: `tool-${messageIdCounter.current++}`,
            tool_name: event.tool_name,
            tool_params: event.tool_params,
            status: 'executing',
            timestamp: new Date(),
          };
          items.push(tool);
          
          return items;
        });
        
        // Clear streaming message
        setStreamingMessage(null);
        streamingMessageRef.current = null;
        setIsStreaming(true);
        break;

      case 'tool_update':
        // Update tool parameters as they stream in
        console.log('Tool update:', event.tool_name, event.tool_params);
        setTimeline((prev) => {
          const updated = [...prev];
          // Find the most recent executing tool with matching name
          for (let i = updated.length - 1; i >= 0; i--) {
            const item = updated[i];
            if (item.itemType === 'tool' && 
                item.tool_name === event.tool_name && 
                item.status === 'executing') {
              updated[i] = {
                ...item,
                tool_params: event.tool_params,
              };
              break;
            }
          }
          return updated;
        });
        break;

      case 'client_tool_request':
        // Agent requests tool execution on frontend
        console.log('Client tool request:', event.tool_name, event.request_id);
        
        // Keep streaming indicator active during tool execution
        setIsStreaming(true);
        
        // Add tool request to pending map
        setPendingToolCalls((prev) => {
          const updated = new Map(prev);
          updated.set(event.request_id, {
            tool_name: event.tool_name,
            tool_params: event.tool_params,
            status: 'pending',
          });
          return updated;
        });
        break;

      case 'tool_result':
        // Tool execution completed - update tool in timeline
        console.log('Tool result:', event.tool_name, event.result);
        setTimeline((prev) => {
          const updated = [...prev];
          // Find the most recent execution for this tool and update it
          for (let i = updated.length - 1; i >= 0; i--) {
            const item = updated[i];
            if (item.itemType === 'tool' && 
                item.tool_name === event.tool_name && 
                item.status === 'executing') {
              updated[i] = {
                ...item,
                status: 'success',
                result: event.result,
              };
              break;
            }
          }
          return updated;
        });
        // Don't set isStreaming to false here - wait for 'done' event
        break;

      case 'done':
        // Message complete - finalize streaming message
        console.log('Received done event, finalizing message');
        setStreamingMessage((currentStreamingMessage) => {
          if (currentStreamingMessage) {
            console.log('Adding message to timeline:', currentStreamingMessage.substring(0, 50) + '...');
            
            const newMessage: TimelineMessage = {
              itemType: 'message',
              id: `assistant-${messageIdCounter.current++}`,
              role: 'assistant',
              content: currentStreamingMessage,
              timestamp: new Date(),
            };
            setTimeline((prev) => {
              // Guard against duplicates (React StrictMode double-invocation)
              const isDuplicate = prev.some(item => 
                item.itemType === 'message' &&
                item.role === 'assistant' && 
                item.content === currentStreamingMessage &&
                Date.now() - item.timestamp.getTime() < 1000
              );
              if (isDuplicate) {
                console.log('Duplicate message detected, skipping');
                return prev;
              }
              return [...prev, newMessage];
            });
          } else {
            console.log('No streaming message to finalize');
          }
          return null;
        });
        
        // Stop streaming indicator
        setIsStreaming(false);
        break;

      case 'error':
        setError(event.error);
        setIsStreaming(false);
        console.error('Assistant error:', event.error);
        break;

      case 'conversation_cleared':
        // Conversation successfully cleared on backend
        console.log('Conversation cleared on backend');
        // Local state is already cleared in clearConversation callback
        break;

      default:
        console.warn('Unknown event type:', (event as any).type);
    }
  }, []); // Empty dependencies - all state updates use functional form

  // Connect to WebSocket
  const connect = useCallback(() => {
    try {
      const url = getWebSocketUrl();
      console.log('Connecting to pipeline assistant WebSocket...');

      setConnectionState((prev) => ({ ...prev, status: 'connecting' }));

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleEvent(data as AssistantEvent);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket closed');
        setConnectionState((prev) => ({
          ...prev,
          status: 'disconnected',
        }));

        // Attempt reconnection
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }

        setConnectionState((prev) => {
          const newRetryCount = prev.retryCount + 1;
          if (newRetryCount <= 5) {
            const delay = Math.min(1000 * Math.pow(2, newRetryCount - 1), 16000);
            console.log(`Reconnecting in ${delay}ms (attempt ${newRetryCount})`);

            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);

            return { ...prev, retryCount: newRetryCount };
          } else {
            return {
              ...prev,
              status: 'error',
              error: 'Max reconnection attempts reached',
            };
          }
        });
      };

      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
        setError('WebSocket connection error');
      };
    } catch (err) {
      console.error('Failed to connect:', err);
      setError(err instanceof Error ? err.message : 'Connection failed');
      setConnectionState((prev) => ({
        ...prev,
        status: 'error',
        error: err instanceof Error ? err.message : 'Connection failed',
      }));
    }
  }, [getWebSocketUrl, handleEvent]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnectionState({
      status: 'disconnected',
      provider: null,
      error: null,
      retryCount: 0,
    });
  }, []);

  // Public actions
  const sendMessage = useCallback(
    (content: string) => {
      // Clear any previous error
      setError(null);
      
      // Add user message to timeline
      const userMessage: TimelineMessage = {
        itemType: 'message',
        id: `user-${messageIdCounter.current++}`,
        role: 'user',
        content,
        timestamp: new Date(),
      };
      setTimeline((prev) => [...prev, userMessage]);

      // Send to WebSocket
      send({ type: 'user_message', content });
    },
    [send]
  );

  const respondToToolRequest = useCallback(
    (requestId: string, success: boolean, result?: any, error?: string) => {
      console.log(`Sending tool response for ${requestId}:`, { success, result, error });
      send({
        type: 'client_tool_response',
        request_id: requestId,
        success,
        result,
        error,
      });

      // Update tool call status
      setPendingToolCalls((prev) => {
        const updated = new Map(prev);
        const toolCall = updated.get(requestId);
        if (toolCall) {
          updated.set(requestId, {
            ...toolCall,
            status: success ? 'success' : 'error',
            result: success ? result : undefined,
            error: error,
          });
        }
        return updated;
      });
    },
    [send]
  );

  const stopStreaming = useCallback(() => {
    console.log('Stopping stream');
    
    // Send stop message to backend
    send({ type: 'stop_streaming' });
    
    // Finalize any current streaming message
    setStreamingMessage((currentStreamingMessage) => {
      if (currentStreamingMessage) {
        console.log('Finalizing interrupted message');
        const newMessage: TimelineMessage = {
          itemType: 'message',
          id: `assistant-${messageIdCounter.current++}`,
          role: 'assistant',
          content: currentStreamingMessage + ' [interrupted]',
          timestamp: new Date(),
        };
        setTimeline((prev) => [...prev, newMessage]);
      }
      return null;
    });
    
    // Stop streaming state
    setIsStreaming(false);
  }, [send]);

  const clearConversation = useCallback(async () => {
    try {
      // Send WebSocket message to clear conversation and reset agent state
      send({ type: 'clear_conversation' });

      // Clear local state immediately
      setTimeline([]);
      setStreamingMessage(null);
      setIsStreaming(false);
      setError(null);
      setPendingToolCalls(new Map());
      
      console.log('Conversation clear requested');
    } catch (err) {
      console.error('Failed to clear conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear conversation');
    }
  }, [send]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected: connectionState.status === 'connected',
    connectionState,
    error,
    timeline,
    streamingMessage,
    isStreaming,
    sendMessage,
    respondToToolRequest,
    stopStreaming,
    clearConversation,
    pendingToolCalls,
  };
}
